import projectService from '../../businessLogic/projectService';
import academyData from '@/data/academyData';
import { getAllUsers } from '@/data/authData';
import coursesData from '@/data/coursesData';

const state = {
  projects: [],
  filteredProjects: [],
  selectedProject: null,
  saveStatus: 'saved', // 'saved', 'unsaved', 'saving', 'error'
};

const getters = {
  projects: (state) => state.projects,
  filteredProjects: state => {
    // Creamos una copia del array para no modificar el original
    return [...state.filteredProjects].sort((a, b) => {
      // Si existe createdAt, ordenamos por fecha
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      // Si no existe createdAt, ordenamos por ID asumiendo que es secuencial
      return b.id - a.id;
    });
  },
  selectedProject: state => state.selectedProject,
  saveStatus: state => state.saveStatus,
};

const mutations = {
  SET_SAVE_STATUS(state, status) {
    state.saveStatus = status;
  },
  ADD_COMPONENT_TEMP(state, component) {
    // Asegúrate de que el proyecto seleccionado tenga un array de componentes
    if (!state.selectedProject) {
      console.error('No hay proyecto seleccionado para añadir componente');
      return;
    }
    
    if (!state.selectedProject.components) {
      state.selectedProject.components = [];
    }
    
    // Añade el componente con bandera de pendiente
    state.selectedProject.components.push({
      ...component,
      _pending: true
    });
  },
  
  setProjects(state, projects) {
    state.projects = projects;
  },
  updateProject(state, updatedProject) {
    const updatedProjects = state.projects.map(proj =>
      proj.id === updatedProject.id ? updatedProject : proj
    );
    state.projects = updatedProjects;
    if (state.selectedProject && state.selectedProject.id === updatedProject.id) {
      state.selectedProject = updatedProject;
    }
  },
  removeProject(state, projectId) {
    state.projects = state.projects.filter(proj => proj.id !== projectId);
  },
  setFilteredProjects(state, filteredProjects) {
    state.filteredProjects = filteredProjects;
  },
  setSelectedProject(state, project) {
    state.selectedProject = project;
  },
  deselectProject(state) {
    state.selectedProject = null;
  },
  UPDATE_COMPONENT_POSITION(state, { componentId, newPosition }) {
    const component = state.selectedProject.components.find(comp => comp.id === componentId);
    if (component) {
      component.x = newPosition.x;
      component.y = newPosition.y;
    }
  },
  SET_PROJECT(state, project) {
    state.selectedProject = project; // Actualiza el proyecto seleccionado en el estado local
  },
  SET_FILTERED_PROJECTS(state, projects) {
    state.filteredProjects = projects;
  },
  REMOVE_CONNECTIONS(state, connectionsToRemove) {
    state.selectedProject.connections = state.selectedProject.connections.filter(connection =>
      !connectionsToRemove.some(toRemove => toRemove.id === connection.id)
    );
  },
  REMOVE_COMPONENT(state, idLocal) {
    state.selectedProject.components = state.selectedProject.components.filter(component => component.idLocal !== idLocal);
  }, 
  SET_COMPONENTS(state, components) {
    state.selectedProject.components = components;
},
  SET_CONNECTIONS(state, connections) {
    state.selectedProject.connections = connections;
  },
  REMOVE_COMPONENTS(state, componentIds) {
    state.projects = state.projects.filter(proj => !componentIds.includes(proj.id));
  },
  UPDATE_PROJECT_COMPONENTS(state, components) {
    if (state.selectedProject) {
      state.selectedProject.components = components;
    }
  },

};

const actions = {

  async duplicateProjectWithData({ dispatch, rootGetters }, originalProject) {
  try {
    const isPracticalGuide = originalProject.id && originalProject.id.startsWith('practical_');
    
    let projectName;
    if (originalProject.id && !isPracticalGuide) {
      // Solo buscar en projectService si es un proyecto real, no una guía práctica
      try {
        const fullProject = await projectService.getProjectById(
          rootGetters['auth/user'].uid, 
          originalProject.id    
        );
        projectName = fullProject.name || 'Proyecto sin título';
      } catch (error) {
        console.warn('No se pudo obtener el proyecto desde projectService, usando nombre original');
        projectName = originalProject.name || 'Proyecto sin título';
      }
    } else {
      // Para guías prácticas y proyectos sin ID, usar el nombre original
      projectName = originalProject.name || 'Proyecto sin título';
    }

    // Crear el objeto templateInfo solo con los campos que existen
    const templateInfo = {};
    if (originalProject.name) templateInfo.name = originalProject.name;
    if (originalProject.InitialProjectDescription) templateInfo.description = originalProject.InitialProjectDescription;
    if (originalProject.introduction) templateInfo.introduction = originalProject.introduction;
    if (originalProject.gif) templateInfo.gif = originalProject.gif;
    if (originalProject.code) templateInfo.code = originalProject.code;
    if (originalProject.titleIntroduction) templateInfo.titleIntroduction = originalProject.titleIntroduction;

    // También guardar components y connections en templateInfo para practicalGuides
    if (isPracticalGuide) {
      if (originalProject.project?.components || originalProject.components) {
        templateInfo.components = originalProject.project?.components || originalProject.components;
      }
      if (originalProject.project?.connections || originalProject.connections) {
        templateInfo.connections = originalProject.project?.connections || originalProject.connections;
      }
    }

    // Crear objeto del nuevo proyecto
    const newProjectData = {
        name: `${projectName} (copia)`,
        category: originalProject.category || 'other',
        imageUrl: originalProject.imageUrl || '', 
        userId: rootGetters['auth/user'].uid,
        createdAt: new Date().toISOString(),
        isActivity: true
    };

    // Solo agregar templateInfo si tiene algún campo
    if (Object.keys(templateInfo).length > 0) {
        newProjectData.templateInfo = templateInfo;
    }

    const { id: newProjectId } = await projectService.createProject(newProjectData);

    // Buscar datos en múltiples ubicaciones
    let internalData;
    if (isPracticalGuide) {
      // Para guías prácticas, buscar en múltiples ubicaciones
      internalData = {
        components: originalProject.project?.components || 
                   originalProject.components || 
                   originalProject.templateInfo?.components || [],
        connections: originalProject.project?.connections || 
                    originalProject.connections || 
                    originalProject.templateInfo?.connections || [],
        code: originalProject.project?.code || 
              originalProject.code || 
              originalProject.templateInfo?.code || {}
      };
    } else {
      // Para proyectos normales
      internalData = {
        components: originalProject.components || [],
        connections: originalProject.connections || [],
        code: originalProject.code || {}
      };
    }

    // Validación correcta para arrays y objetos
    const hasComponents = Array.isArray(internalData.components) && internalData.components.length > 0;
    const hasConnections = Array.isArray(internalData.connections) && internalData.connections.length > 0;
    const hasCode = internalData.code && 
                   ((typeof internalData.code === 'string' && internalData.code.trim() !== '') ||
                    (typeof internalData.code === 'object' && Object.keys(internalData.code).length > 0));

    // Solo actualizar el proyecto si hay datos internos válidos
    if (hasComponents || hasConnections || hasCode) {
      console.log(' Actualizando proyecto con datos internos:', {
        projectId: newProjectId,
        components: internalData.components?.length || 0,
        connections: internalData.connections?.length || 0,
        hasCode: !!hasCode,
        isPracticalGuide: isPracticalGuide
      });
      
      await projectService.updateProject(newProjectId, internalData);
    } else {
      console.log(' No hay datos internos para actualizar en el proyecto');
      console.log('Estructura del proyecto original:', {
        id: originalProject.id,
        hasProject: !!originalProject.project,
        hasComponents: !!originalProject.components,
        hasConnections: !!originalProject.connections,
        hasCode: !!originalProject.code,
        hasTemplateInfo: !!originalProject.templateInfo
      });
    }

    await dispatch('fetchProjects');

    return newProjectId;
  } catch (error) {
    console.error("Error al duplicar el proyecto:", error);
    throw error;
  }
},

  async setProjectManually({ commit, dispatch }, project) {
    commit('SET_PROJECT', project);
    try {
        await dispatch('updateProject', { projectId: project.id, updateData: project }); // ✅ Corrección
    } catch (error) {
        console.error("Error al guardar el proyecto en la base de datos:", error);
    }
},
  async updateProjectImage({ commit, dispatch }, { id, formData }) {
    try {
      // Subir la imagen primero
      const imageUrl = await projectService.uploadImageToProject(formData.get('image'));
      
      // Actualizar el proyecto con la nueva URL de la imagen
      const updateData = {
        imageUrl: imageUrl
      };
      
      await dispatch('updateProject', { 
        projectId: id, 
        updateData: updateData 
      });
      
      return imageUrl;
    } catch (error) {
      console.error('Error updating project image:', error);
      throw error;
    }
  },

  async updateProjectName({ dispatch }, { id, name }) {
    try {
      const updateData = {
        name: name
      };
      
      await dispatch('updateProject', { 
        projectId: id, 
        updateData: updateData 
      });
    } catch (error) {
      console.error('Error updating project name:', error);
      throw error;
    }
  },
  
  async fetchProjects({ commit, rootGetters }) {
    try {
      const userId = rootGetters['auth/user'].uid;
      const projects = await projectService.getProjectsByUserId(userId);
      const groupActivityIds = await academyData.getStudentGroupActivities(userId);
      
      // Obtener los IDs de las actividades de tutor
      const tutorActivityIds = await academyData.getTutorGroupActivities(userId);
        // 🔥 Traer todos los usuarios
      const allUsers = await getAllUsers();

      // 🔁 Enriquecer proyectos con nombre del dueño
      const enrichedProjects = projects.map(project => {
        const userInfo = allUsers[project.userId] || {};
        return {
          ...project,
          ownerName: userInfo.name || userInfo.displayName || 'Desconocido'
        };
      });
      const filteredProjects = enrichedProjects.filter(project => 
        !groupActivityIds.includes(project.id) && 
        !tutorActivityIds.includes(project.id) && 
        (project.userId === userId || project.ownerId === userId || (project.permissions && project.permissions[userId] === 'owner'))
      );
      
      commit('setProjects', filteredProjects);
      commit('setFilteredProjects', filteredProjects);
    } catch (error) {
      console.error('[ERROR] Error en fetchProjects:', error);
      throw error;
    }
  },
  async createProject({ commit, rootGetters }, projectData) {
    try {
        console.log('[STORE] Creando proyecto:', projectData);
        
        const projectWithMetadata = {
            ...projectData,
            userId: rootGetters['auth/user'].uid,
            createdAt: projectData.createdAt || new Date().toISOString() // Respetar createdAt si ya existe
        };
        
        const result = await projectService.createProject(projectWithMetadata);
        console.log('[STORE] Proyecto creado:', result);
        
        const projects = await projectService.getProjectsByUserId(projectWithMetadata.userId);
        commit('setProjects', projects);
        
        // Establecer el proyecto recién creado como selectedProject
        const createdProject = { id: result.id || result, ...projectWithMetadata };
        commit('setSelectedProject', createdProject);
        
        return result.id || result; // Manejo flexible del retorno
    } catch (error) {
        console.error('[STORE] Error al crear proyecto:', error);
        throw error;
    }
},
  async updateProject({ commit, state }, { projectId, projectData, updateData }) {
    // Validaciones primero
    if (!projectId) {
        console.error('[STORE] Project ID is undefined');
        return;
    }

    if (projectId.startsWith('practical_') || projectId.startsWith('activity') || state.selectedProject?.isReadOnly) {
        console.warn('[STORE] Intento de guardar un practicalGuide o activity, operación ignorada.');
        return;
    }

    // Aceptar tanto projectData como updateData para compatibilidad
    const dataToUpdate = projectData || updateData;
    
    if (!dataToUpdate) {
        console.error('[STORE] Project data is undefined');
        return;
    }

    if (state.selectedProject?.isTemplateEditing) {
        console.log('[STORE] Proyecto en modo template editing, saltando actualización');
        return projectId;
    }

    try {
        console.log('[STORE] Actualizando proyecto:', projectId, dataToUpdate);
        
        await projectService.updateProject(projectId, dataToUpdate);
        console.log('[STORE] Proyecto actualizado exitosamente');
        
        if (state.selectedProject && state.selectedProject.id === projectId) {
            commit('updateProject', { ...state.selectedProject, ...dataToUpdate });
        }
        
        return projectId;
    } catch (error) {
        console.error('[STORE] Error al actualizar proyecto:', error);
        throw error;
    }
},
  
  
  async deleteProject({ commit }, projectId) {
    try {
      await projectService.deleteProject(projectId);
      commit('removeProject', projectId);
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },
  filterProjects({ commit, state }, filter) {
    const normalizedFilter = filter.toLowerCase().trim();
    let filteredProjects;
    
    if (!normalizedFilter) {
      filteredProjects = state.projects;
    } else {
      filteredProjects = state.projects.filter(project =>
        project.name.toLowerCase().includes(normalizedFilter)
      );
    }
    
    // Ordenar los proyectos filtrados del más reciente al más antiguo
    filteredProjects = filteredProjects.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return b.id - a.id;
    });
    
    commit('setFilteredProjects', filteredProjects);
  },
  selectProject({ dispatch }, projectId) {
    dispatch('fetchProjectById', projectId);
  },
  deselectProject({ commit }) {
    commit('deselectProject');
  },
async fetchProjectById({ commit, rootState }, projectId) {
  if (!projectId) {
    console.error('No project ID provided.');
    return; // Detiene la ejecución si el ID del proyecto no está definido
  }

  try {
    const userId = rootState.auth.user.uid;  // Obtén el ID del usuario desde el estado raíz
    console.log(`[DEBUG] Fetching project with ID: ${projectId} for user: ${userId}`);
    
    // Verificar si es un starterGuide o practicalGuide
    if (projectId.startsWith('starter_') || projectId.startsWith('practical_')) {
      console.log(`[DEBUG] Es una guía práctica/starter, usando academyData`);
      
      // Para guías prácticas, usar academyData
      const guide = await academyData.getStarterGuideById(projectId);
      
      if (guide) {
        // Convertir la guía a formato de proyecto para compatibilidad
        const projectData = {
          id: guide.id,
          name: guide.name,
          code: guide.code || guide.project?.code || '',
          components: guide.components || guide.project?.components || [],
          connections: guide.connections || guide.project?.connections || [],
          isTemplateEditing: true,
          templateInfo: {
            name: guide.name,
            description: guide.InitialProjectDescription,
            code: guide.code || guide.project?.code || '',
            introduction: guide.introduction || [],
            titleIntroduction: guide.titleIntroduction || ''
          },
          steps: guide.steps || [],
          InitialProjectDescription: guide.InitialProjectDescription,
          introduction: guide.introduction || [],
          titleIntroduction: guide.titleIntroduction || '',
          category: guide.category,
          difficulty: guide.difficulty,
          estimatedTime: guide.estimatedTime,
          isPublished: guide.isPublished || false
        };
        
        console.log("[DEBUG] Guía convertida a proyecto:", projectData);
        commit('setSelectedProject', projectData);
      } else {
        console.warn(`Guía con ID '${projectId}' no encontrada.`);
      }
    } else {
      // Para proyectos normales, usar projectService
      const project = await projectService.getProjectById(userId, projectId);
      console.log("[DEBUG] Proyecto obtenido:", project);
      
      if (project) {
        commit('setSelectedProject', project); // Guarda el proyecto en el estado
      } else {
        console.warn(`Proyecto con ID '${projectId}' no encontrado en la colección 'projects'.`);
      }
    }
  } catch (error) {
    console.error(`Error fetching project by ID: ${projectId}. Ensure that project exists and belongs to the user.`, error);
  }
}

  ,
  // Obtener el siguiente ID de componente disponible
  async getNextAvailableComponentId({ dispatch }, { projectId, prefix }) {
    const nextId = await dispatch('fetchNextAvailableComponentId', { projectId, prefix });
    return nextId;
  },

  // Fetch del siguiente ID disponible desde el servicio
  async fetchNextAvailableComponentId(_, { projectId, prefix }) {
    return await projectService.getNextAvailableComponentId(projectId, prefix);
  },

  async addComponentToProject({ dispatch }, { projectId, component }) {
    try {
      await projectService.addComponentToProject(projectId, component);
      await dispatch('fetchProjectById', projectId);
    } catch (error) {
      console.error('Error adding component to project:', error); 
      throw error;
    }
  },
  async removeComponentFromProject({ commit, state }, { projectId, idLocal }) {
    try {
        const result = await projectService.removeComponentFromProject(projectId, idLocal);

        if (result.success) {
            // Actualizar el estado local eliminando el componente
            const updatedComponents = state.selectedProject.components.filter(comp => comp.id !== idLocal);
            commit('SET_COMPONENTS', updatedComponents);

        } else {
            console.error(`No se pudo eliminar el componente ${idLocal}`);
        }
    } catch (error) {
        console.error(`Error al eliminar el componente ${idLocal}:`, error);
        throw error;
    }
},
  async uploadProjectImage(_, file) {
    const url = await projectService.uploadImageToProject(file);
    return url;
  },
   async updateComponentPosition({ commit, state, dispatch }, { componentId, newPosition }) {
    if (state.selectedProject?.isTemplateEditing) {
      const component = state.selectedProject.components.find(comp => comp.id === componentId);
      if (component) {
        component.x = newPosition.x;
        component.y = newPosition.y;
      }
      return; 
    }

    let projectId = state.selectedProject?.id;

    // El warning original es útil, pero ahora tenemos una guarda para el caso más problemático.
    if (!projectId) {
      console.error('Project ID not provided and not found in state. Update aborted.');
      return; 
    }
  
    // Verifica que el proyecto esté cargado y coincida con `projectId`
    if (!state.selectedProject || state.selectedProject.id !== projectId) {
      console.warn('Selected project does not match the provided ID. Fetching project.');
      try {
        await dispatch('fetchProjectById', projectId);
        if (!state.selectedProject || state.selectedProject.id !== projectId) {
          console.error('Project not found after fetching. Update aborted.');
          return;
        }
      } catch (error) {
        console.error('Error fetching project by ID:', error);
        return;
      }
    }
  
    // Actualiza la posición del componente localmente
    commit('UPDATE_COMPONENT_POSITION', { componentId, newPosition });
  
    try {
      // Guarda la actualización en el backend (solo para proyectos de usuario)
      await projectService.updateProject(state.selectedProject.id, {
        components: state.selectedProject.components
      });
    } catch (error) {
      console.error('Error updating project in Firebase:', error);
    }
  },
  
  async updateConnections({ commit, state }, connections) {
    if (state.selectedProject?.isTemplateEditing) {
      return; 
    }
    if (!state.selectedProject) {
      console.error("No project selected.");
      return;
    }
  
    // Verificar si el proyecto tiene ID antes de intentar actualizar en la base de datos
    if (state.selectedProject.id) {
      try {
        const updatedProject = {
          ...state.selectedProject,
          connections,
        };
        
        // Actualiza el proyecto en la base de datos
        await projectService.updateProject(state.selectedProject.id, { connections });
        
        // Actualiza el proyecto en el estado
        commit('SET_PROJECT', updatedProject);
      } catch (error) {
        console.error("Error updating connections in Firebase:", error);
      }
    } else {
      // Si no tiene ID, actualiza el proyecto en el estado sin guardar en la base de datos
      commit('SET_PROJECT', { ...state.selectedProject, connections });
    }
  }
  ,
  
  async removeConnections({ commit }, { projectId, connectionsToRemove }) {
    try {
      await projectService.removeConnectionsFromProject(projectId, connectionsToRemove);
      commit('REMOVE_CONNECTIONS', connectionsToRemove);
    } catch (error) {
      console.error('Error removing connections:', error);
    }
  },

  async getProjectById({ commit }, projectId) {
    try {
      console.log("[DEBUG] Obteniendo proyecto por ID:", projectId);
      const project = await academyData.getActivityById(projectId);
      console.log("[DEBUG] Proyecto obtenido:", project);
      return project;
    } catch (error) {
      console.error("[DEBUG] Error al obtener proyecto:", error);
      throw error;
    }
  },

  async duplicateActivityTemplate({ dispatch, rootGetters }, originalProject) {
  try {
    // Determinar si es una guía práctica
    const isPracticalGuide = originalProject.id && originalProject.id.startsWith('practical_');
    
    const projectName = originalProject.name || 'Proyecto sin título';

    // Crear templateInfo con todos los campos como en duplicateProjectWithData
    const templateInfo = {
      name: originalProject.name,
      description: originalProject.InitialProjectDescription || '',
      introduction: originalProject.introduction || [],
      code: originalProject.code || '',
      componentsDiagram: originalProject.componentsDiagram || '',
      titleIntroduction: originalProject.titleIntroduction || ''
    };

    // AGREGAR: También guardar components y connections en templateInfo para practicalGuides
    if (isPracticalGuide) {
      if (originalProject.project?.components || originalProject.components) {
        templateInfo.components = originalProject.project?.components || originalProject.components;
      }
      if (originalProject.project?.connections || originalProject.connections) {
        templateInfo.connections = originalProject.project?.connections || originalProject.connections;
      }
    }

    const newProjectData = {
      name: `${projectName} - ${rootGetters['auth/user'].displayName || 'Estudiante'}`,
      category: 'activity',
      imageUrl: originalProject.imageUrl || '', 
      userId: rootGetters['auth/user'].uid,
      createdAt: new Date().toISOString(),
      isActivity: true,
      templateInfo: templateInfo,
      steps: originalProject.steps || [] 
    };

    const { id: newProjectId } = await projectService.createProject(newProjectData);

    // ✅ CORRECCIÓN: Buscar datos en múltiples ubicaciones como en duplicateProjectWithData
    let internalData;
    if (isPracticalGuide) {
      internalData = {
        components: originalProject.project?.components || 
                   originalProject.components || 
                   originalProject.templateInfo?.components || [],
        connections: originalProject.project?.connections || 
                    originalProject.connections || 
                    originalProject.templateInfo?.connections || [],
        code: originalProject.project?.code || 
              originalProject.code || 
              originalProject.templateInfo?.code || {}
      };
    } else {
      // Para proyectos normales (fallback original)
      const projectData = originalProject.project || {};
      internalData = {
        components: Array.isArray(projectData.components) ? projectData.components : [],
        connections: Array.isArray(projectData.connections) ? projectData.connections : [],
        code: projectData.code || {}
      };
    }

    // ✅ CORRECCIÓN: Validación correcta para arrays y objetos
    const hasComponents = Array.isArray(internalData.components) && internalData.components.length > 0;
    const hasConnections = Array.isArray(internalData.connections) && internalData.connections.length > 0;
    const hasCode = internalData.code && 
                   ((typeof internalData.code === 'string' && internalData.code.trim() !== '') ||
                    (typeof internalData.code === 'object' && Object.keys(internalData.code).length > 0));

    // Solo actualizar el proyecto si hay datos internos válidos
    if (hasComponents || hasConnections || hasCode) {
      console.log('Actualizando template de actividad con datos internos:', {
        projectId: newProjectId,
        components: internalData.components?.length || 0,
        connections: internalData.connections?.length || 0,
        hasCode: !!hasCode,
        isPracticalGuide: isPracticalGuide
      });
      
      await projectService.updateProject(newProjectId, internalData);
    } else {
      console.log(' No hay datos internos para actualizar en el template de actividad');
      console.log('Estructura del proyecto original:', originalProject);
    }

    await dispatch('fetchProjects');
    return newProjectId;
  } catch (error) {
    console.error("Error al duplicar el template de actividad:", error);
    throw error;
  }
},
  async saveUserProjectCanvas({ commit }, projectData) {
    if (!projectData || !projectData.id) {
      return;
    }
    
    commit('SET_PROJECT', projectData);
    
    try {
      const dataToUpdateInFirestore = {
        components: projectData.components || [],
        connections: projectData.connections || [],
        code: projectData.code || '# Escribe el código aquí',
      };
      
      await projectService.updateProject(projectData.id, dataToUpdateInFirestore);
      
    } catch (error) {
      console.error(`Error al guardar el proyecto ${projectData.id} en Firestore:`, error);
    }
  },
  async updateStarterGuideProject({ commit, state }, { guideId, projectData }) {
    if (!guideId) {
      console.error("Error: No se proporcionó un 'guideId' para actualizar la starterGuide.");
      return;
    } 
    commit('SET_PROJECT', projectData);
    try {
      const dataToUpdateInFirestore = {
        project: {
          components: projectData.components || [],
          connections: projectData.connections || [],
          code: projectData.code || '# Escribe el código aquí',
        }
      }; 
      await coursesData.updateStarterGuide(guideId, dataToUpdateInFirestore);
    } catch (error) {
      console.error(`Error al guardar la starterGuide ${guideId} en Firestore:`, error);
    }
  },
  updateProjectComponents({ commit }, components) {
    commit('UPDATE_PROJECT_COMPONENTS', components);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};